/**
 * 总览看板 —— 「员工使用时长排名」卡片。
 *
 * ============================== 需求背景 ==============================
 * 老板打开总览看板就要能看到「谁用得最多」的摘要（默认前 10 名），并支持切换时间范围、
 * 点进完整的使用分析页。总览只放摘要，不放大图表，避免把首页的信息层级打乱。
 *
 * ============================== 数据口径 ==============================
 * 调用 GET /api/usage/leaderboard，它内部走与使用分析页、导出完全相同的后端统计函数，
 * 因此三处数字必然一致（验收项 7）。前端只负责格式化。
 *
 * ============================== 权限 ==============================
 * 无权限（不在 /admin/usage 模块可见范围内、或后端返回 403）时**整卡不渲染**，
 * 不显示空壳或「无权限」占位，避免在首页留下无效信息。
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Loader2, Trophy } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { fmtDuration } from "../../lib/usageFormat";

const RANGES = [
  { key: "today", zh: "今天", en: "Today" },
  { key: "7d", zh: "近 7 天", en: "7 days" },
  { key: "30d", zh: "近 30 天", en: "30 days" },
  { key: "90d", zh: "近 90 天", en: "90 days" },
];

export default function UsageRankCard() {
  const { lang, api, user, moduleGranted, moduleDenied } = useApp();
  const zh = lang === "zh";
  const navigate = useNavigate();
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  /* 可见性判定必须与侧栏菜单**完全同源**（Layout 的 vis(key, def)）：
       显式屏蔽 → 不显示；显式授予 → 显示；都没有 → 按入口的默认角色（def）判定。
     之前只调用 moduleGranted(key)：/admin/usage 是新增模块，权限矩阵里没有历史授予记录，
     admin / CEO 在矩阵中拿到空数组，结果侧栏因 def 兜底能看到「使用分析」，
     首页这张卡却被误判成无权限而不渲染。改成同一套判定后两边一致。
     角色清单与 server.py MODULE_CATALOG / Layout.isUsageViewer 三处严格一致。 */
  const roleAllows = ["admin", "ceo", "eng_manager", "design_manager", "sales_vp",
    "sales_manager", "branch_sales_manager", "marketing_manager", "warehouse_manager"]
    .includes(user?.baseRole || user?.role || "");
  const explicitDenied = typeof moduleDenied === "function" ? moduleDenied("/admin/usage") : false;
  const explicitAllowed = typeof moduleGranted === "function" ? moduleGranted("/admin/usage") : false;
  const allowed = explicitDenied ? false : (explicitAllowed || roleAllows);

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get("/usage/leaderboard", { params: { range, limit: 10 } });
      setData(d);
      setForbidden(false);
    } catch (e) {
      // 403 = 无权限：整卡不渲染；其它错误保留上一次成功的数据，避免首页闪空。
      if (e?.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
  }, [api, range, allowed]);

  useEffect(() => { load(); }, [load]);

  if (!allowed || forbidden) return null;

  const items = data?.items || [];

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 sm:p-5" data-testid="usage-rank-card" aria-labelledby="usage-rank-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="usage-rank-title" className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Trophy className="size-4 text-amber-500" aria-hidden="true" />
            {zh ? "员工使用时长排名" : "Employee usage ranking"}
          </h2>
          <p className="mt-0.5 text-xs leading-5 text-slate-500">
            {zh ? "按日均有效使用时长 · 仅统计前台可见且有操作的时间 · 不含 admin / CEO" : "Daily effective time · foreground + activity only · admin/CEO excluded"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="usage-rank-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{zh ? r.zh : r.en}</SelectItem>)}
            </SelectContent>
          </Select>
          <button type="button" onClick={() => navigate("/admin/usage")} data-testid="usage-rank-open"
            className="inline-flex min-h-8 items-center gap-1 rounded-md px-2 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
            {zh ? "查看完整分析" : "Full analysis"}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex min-h-24 items-center justify-center text-slate-300" data-testid="usage-rank-loading">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        </div>
      ) : items.length === 0 ? (
        <div className="mt-3 flex min-h-20 flex-col items-center justify-center rounded-lg bg-slate-50 px-4 py-4 text-center" data-testid="usage-rank-empty">
          <Clock className="size-4 text-slate-300" aria-hidden="true" />
          <p className="mt-1.5 text-xs text-slate-500">{zh ? "该时间段还没有员工使用记录" : "No usage recorded in this period"}</p>
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[560px] text-xs" data-testid="usage-rank-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] text-slate-500">
                <th className="w-8 py-2 pr-2 font-medium">#</th>
                <th className="py-2 pr-3 font-medium">{zh ? "员工" : "Employee"}</th>
                <th className="py-2 pr-3 font-medium">{zh ? "部门" : "Dept"}</th>
                <th className="py-2 pr-3 text-right font-medium">{zh ? "日均有效时长" : "Daily avg"}</th>
                <th className="py-2 pr-3 text-right font-medium">{zh ? "活跃天数" : "Active days"}</th>
                <th className="py-2 text-right font-medium">{zh ? "最后活跃" : "Last active"}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.username} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
                  data-testid={`usage-rank-item-${r.username}`} onClick={() => navigate("/admin/usage")}>
                  <td className="py-1.5 pr-2 tabular-nums text-slate-400">{r.rank}</td>
                  <td className="py-1.5 pr-3 font-medium text-slate-800">
                    {r.name}
                    {r.sampleInsufficient && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-700">{zh ? "样本不足" : "low sample"}</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-slate-600">{r.departmentName}</td>
                  <td className="py-1.5 pr-3 text-right font-semibold tabular-nums text-slate-800">{fmtDuration(r.avgDailyEffSec, zh)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-slate-600">{r.activeDays}</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-500">{r.lastActiveAt ? new Date(r.lastActiveAt).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.range?.tz && (
        <p className="mt-2 text-[10px] text-slate-400">
          {data.range.tz === "Asia/Shanghai" ? (zh ? "中国时间" : "China time") : (zh ? "美国太平洋时间" : "US Pacific")}
          {data.preciseSince ? ` · ${zh ? "精确统计自" : "since"} ${new Date(data.preciseSince).toLocaleDateString()}` : ""}
        </p>
      )}
    </section>
  );
}
